import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ChevronLeft, ShieldCheck, Store, Package, ShoppingBag, Wallet, FileText,
  CheckCircle, XCircle, Ban, Star, MapPin, Phone, Mail, TrendingUp, ExternalLink,
} from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import { ReasonModal } from '@/components/ReasonModal';
import { REJECTION_REASONS, SUSPENSION_REASONS } from '@/utils/moderation-reasons';
import { adminApi } from '@/services/api';
import { formatNPRCompact, formatDate } from '@/utils/format';
import {
  Badge, Button, Card, CardContent, Separator, Skeleton, cn,
} from '@nepal-ecom/ui';

type VendorStatus = 'pending' | 'under_review' | 'approved' | 'rejected' | 'suspended';
type Tier = 'basic' | 'silver' | 'gold' | 'platinum';

interface VendorDetailResponse {
  id: string;
  businessName: string;
  ownerName: string;
  slug: string;
  description: string | null;
  panNumber: string | null;
  vatNumber: string | null;
  documents: Record<string, string> | null;
  phone: string | null;
  email: string | null;
  bannerUrl: string | null;
  logoUrl: string | null;
  verificationStatus: VendorStatus;
  tier: Tier;
  isActive: boolean;
  rating: number | string;
  totalReviews: number;
  totalSales: number;
  totalOrders: number;
  productCount: number;
  provinceName: string | null;
  district: string | null;
  createdAt: string;
  rejectionReason: string | null;
  recentOrders: Array<{
    id: string;
    orderNumber: string;
    status: string;
    total: number;
    createdAt: string;
  }>;
}

const STATUS_META: Record<VendorStatus, { label: string; variant: 'success' | 'warning' | 'info' | 'destructive' | 'secondary' }> = {
  pending: { label: 'Pending', variant: 'warning' },
  under_review: { label: 'Under Review', variant: 'info' },
  approved: { label: 'Verified', variant: 'success' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  suspended: { label: 'Suspended', variant: 'secondary' },
};

const DOC_LABELS: Record<string, string> = {
  pan: 'PAN / VAT certificate',
  panCard: 'PAN / VAT certificate',
  businessRegistration: 'Business registration',
  citizenshipFront: 'Citizenship — front',
  citizenshipBack: 'Citizenship — back',
};

const docLabel = (key: string) =>
  DOC_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const isImageDoc = (url: string) => /\.(jpe?g|png|webp|gif|avif)(\?|$)/i.test(url);

// KYC docs live in the private MinIO bucket. We resolve the stored object key
// into a short-lived, authenticated view URL (streamed through the API) rather
// than linking the raw key. `storedValue` is the key (used for extension/type)
// and to detect legacy public-path values.
function KycDocCard({ vendorId, docKey, storedValue }: {
  vendorId: string; docKey: string; storedValue: string;
}) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    adminApi
      .getKycViewUrl(vendorId, docKey)
      .then((u) => { if (active) { setUrl(u); if (!u) setFailed(true); } })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [vendorId, docKey]);

  const isImg = isImageDoc(storedValue);
  return (
    <a
      href={url || undefined}
      target="_blank"
      rel="noopener noreferrer"
      aria-disabled={!url}
      onClick={(e) => { if (!url) e.preventDefault(); }}
      className="group block rounded-xl border border-slate-200 overflow-hidden hover:border-primary-400 hover:shadow-sm transition"
    >
      <div className="aspect-[4/3] bg-slate-100 flex items-center justify-center overflow-hidden">
        {isImg && url ? (
          <img src={url} alt={docLabel(docKey)} className="w-full h-full object-cover" />
        ) : (
          <FileText className={cn('h-10 w-10', failed ? 'text-red-300' : 'text-slate-400')} />
        )}
      </div>
      <div className="flex items-center justify-between gap-2 px-3 py-2.5">
        <span className="text-sm font-medium text-slate-800 truncate">
          {docLabel(docKey)}{failed && <span className="text-red-500"> · unavailable</span>}
        </span>
        <ExternalLink className="h-3.5 w-3.5 text-slate-400 group-hover:text-primary-600 flex-shrink-0" />
      </div>
    </a>
  );
}

const TIER_META: Record<Tier, { label: string; tint: string }> = {
  basic:    { label: 'Basic',    tint: 'bg-slate-100 text-slate-700' },
  silver:   { label: 'Silver',   tint: 'bg-slate-200 text-slate-800' },
  gold:     { label: 'Gold',     tint: 'bg-amber-100 text-amber-800' },
  platinum: { label: 'Platinum', tint: 'bg-emerald-100 text-emerald-800' },
};

type Tab = 'overview' | 'orders' | 'kyc';
const TABS: Array<{ key: Tab; label: string; icon: typeof Store }> = [
  { key: 'overview', label: 'Overview', icon: Store },
  { key: 'orders',   label: 'Orders',   icon: ShoppingBag },
  { key: 'kyc',      label: 'KYC docs', icon: FileText },
];

function StatTile({
  icon: Icon, label, value, tone = 'primary',
}: { icon: typeof Store; label: string; value: string; tone?: 'primary' | 'emerald' | 'cobalt' | 'amber' }) {
  const tones = {
    primary: 'bg-primary-50 ring-primary-100 text-primary-600',
    emerald: 'bg-emerald-50 ring-emerald-100 text-emerald-600',
    cobalt:  'bg-cobalt-50 ring-cobalt-100 text-cobalt-600',
    amber:   'bg-amber-50 ring-amber-100 text-amber-700',
  };
  return (
    <Card>
      <CardContent className="p-5">
        <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center ring-1', tones[tone])}>
          <Icon className="h-5 w-5" />
        </div>
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mt-3">{label}</p>
        <p className="text-2xl font-bold text-slate-900 tabular-nums mt-0.5">{value}</p>
      </CardContent>
    </Card>
  );
}

export function VendorDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const [bannerBroken, setBannerBroken] = useState(false);
  const [modal, setModal] = useState<'reject' | 'suspend' | null>(null);

  const { data: vendor, isLoading, isError } = useQuery({
    queryKey: ['admin-vendor', id],
    queryFn: () => adminApi.getVendor(id),
    select: (res) => res.data.data as VendorDetailResponse,
    enabled: !!id,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-vendor', id] });

  const approveMutation = useMutation({
    mutationFn: () => adminApi.approveVendor(id),
    onSuccess: () => { invalidate(); toast.success('Vendor approved'); },
    onError: (e: any) => toast.error('Approve failed', { description: e?.response?.data?.message }),
  });
  const rejectMutation = useMutation({
    mutationFn: (reason: string) => adminApi.rejectVendor(id, reason),
    onSuccess: () => { invalidate(); toast.success('Vendor rejected'); },
    onError: (e: any) => toast.error('Reject failed', { description: e?.response?.data?.message }),
  });
  const suspendMutation = useMutation({
    mutationFn: (reason: string) => adminApi.suspendVendor(id, reason),
    onSuccess: () => { invalidate(); toast.success('Vendor suspended'); },
    onError: (e: any) => toast.error('Suspend failed', { description: e?.response?.data?.message }),
  });
  const reactivateMutation = useMutation({
    mutationFn: () => adminApi.reactivateVendor(id),
    onSuccess: () => { invalidate(); toast.success('Vendor reactivated'); },
    onError: (e: any) => toast.error('Reactivate failed', { description: e?.response?.data?.message }),
  });

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-48 w-full" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  if (isError || !vendor) {
    return (
      <div className="animate-fade-in">
        <Link to="/vendors" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ChevronLeft className="h-4 w-4" /> All vendors
        </Link>
        <EmptyState icon={Store} title="Vendor not found" description="This vendor doesn't exist or was removed." />
      </div>
    );
  }

  const statusMeta = STATUS_META[vendor.verificationStatus];
  const tierMeta = TIER_META[vendor.tier];
  const canModerate = vendor.verificationStatus === 'pending' || vendor.verificationStatus === 'under_review';
  const rating = typeof vendor.rating === 'string' ? parseFloat(vendor.rating) : vendor.rating;

  const handleReject = () => setModal('reject');
  const handleSuspend = () => setModal('suspend');

  return (
    <div className="animate-fade-in space-y-6">
      <Link to="/vendors" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ChevronLeft className="h-4 w-4" /> All vendors
      </Link>

      <Card className="overflow-hidden">
        <div className="relative h-32 bg-gradient-to-br from-primary-100 to-primary-50">
          {!bannerBroken && vendor.bannerUrl && (
            <img
              src={vendor.bannerUrl}
              alt=""
              onError={() => setBannerBroken(true)}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        </div>
        <CardContent className="p-5 sm:p-6 pt-3">
          <div className="relative z-10 flex items-start gap-4 -mt-12 sm:-mt-14">
            <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl ring-4 ring-white bg-slate-100 overflow-hidden shadow-card flex-shrink-0 flex items-center justify-center">
              {vendor.logoUrl
                ? <img src={vendor.logoUrl} alt="" className="w-full h-full object-cover" />
                : <Store className="h-9 w-9 text-slate-400" />}
            </div>
            <div className="flex-1 min-w-0 pt-9 sm:pt-11">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight">{vendor.businessName}</h1>
                    <Badge variant={statusMeta.variant} className="gap-1">
                      {vendor.verificationStatus === 'approved' && <ShieldCheck className="h-3 w-3" />}
                      {statusMeta.label}
                    </Badge>
                    <span className={cn('text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md', tierMeta.tint)}>
                      {tierMeta.label}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    {vendor.ownerName}
                    {vendor.district && <> · {vendor.district}{vendor.provinceName ? `, ${vendor.provinceName}` : ''}</>}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {canModerate && (
                    <>
                      <Button variant="outline" onClick={handleReject} disabled={rejectMutation.isPending}>
                        <XCircle className="h-4 w-4" /> Reject
                      </Button>
                      <Button onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}>
                        <CheckCircle className="h-4 w-4" /> Approve
                      </Button>
                    </>
                  )}
                  {vendor.verificationStatus === 'approved' && (
                    <Button variant="outline" onClick={handleSuspend} disabled={suspendMutation.isPending}>
                      <Ban className="h-4 w-4" /> Suspend
                    </Button>
                  )}
                  {vendor.verificationStatus === 'suspended' && (
                    <Button onClick={() => reactivateMutation.mutate()} disabled={reactivateMutation.isPending}>
                      <CheckCircle className="h-4 w-4" /> Reinstate
                    </Button>
                  )}
                </div>
              </div>
              {vendor.description && (
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{vendor.description}</p>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                {!!rating && (
                  <span className="inline-flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" />
                    <span className="font-semibold text-slate-800 tabular-nums">{rating.toFixed(1)}</span>
                    <span>({vendor.totalReviews})</span>
                  </span>
                )}
                {vendor.district && (
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {vendor.district}</span>
                )}
                {vendor.phone && (
                  <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {vendor.phone}</span>
                )}
                {vendor.email && (
                  <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {vendor.email}</span>
                )}
                <span>Joined {formatDate(vendor.createdAt)}</span>
              </div>

              {vendor.verificationStatus === 'rejected' && vendor.rejectionReason && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs font-semibold text-red-800">Rejection reason</p>
                  <p className="text-sm text-red-700 mt-1">{vendor.rejectionReason}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatTile icon={TrendingUp} label="Lifetime GMV" value={formatNPRCompact(vendor.totalSales)} tone="emerald" />
        <StatTile icon={ShoppingBag} label="Orders" value={vendor.totalOrders.toLocaleString()} tone="cobalt" />
        <StatTile icon={Package} label="Products" value={vendor.productCount.toLocaleString()} tone="primary" />
        <StatTile icon={Star} label="Rating" value={rating ? `${rating.toFixed(1)} / 5` : '—'} tone="amber" />
      </div>

      <div className="border-b border-slate-200">
        <nav className="flex gap-1 overflow-x-auto scrollbar-hide">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 h-11 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                  active
                    ? 'text-primary-700 border-primary-600'
                    : 'text-slate-600 hover:text-slate-900 border-transparent',
                )}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <Card className="lg:col-span-2">
            <CardContent className="p-5 space-y-4">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Recent orders</h3>
              {vendor.recentOrders.length === 0 ? (
                <p className="text-sm text-slate-500 py-8 text-center">No orders yet.</p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {vendor.recentOrders.slice(0, 5).map((o) => (
                    <li key={o.id} className="flex items-center justify-between py-3">
                      <div>
                        <button
                          type="button"
                          onClick={() => navigate(`/orders/${o.id}`)}
                          className="text-sm font-semibold text-slate-900 hover:text-primary-600 tabular-nums"
                        >
                          #{o.orderNumber}
                        </button>
                        <p className="text-xs text-slate-500">{formatDate(o.createdAt)}</p>
                      </div>
                      <Badge variant="secondary" className="capitalize">{o.status.replace(/_/g, ' ')}</Badge>
                      <span className="text-sm font-bold text-slate-900 tabular-nums">{formatNPRCompact(o.total)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 space-y-3">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Quick info</h3>
              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">PAN</span>
                  <span className="font-mono font-semibold text-slate-900 tabular-nums">{vendor.panNumber ?? '—'}</span>
                </div>
                <Separator />
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">VAT</span>
                  <span className="font-mono font-semibold text-slate-900 tabular-nums">{vendor.vatNumber ?? '—'}</span>
                </div>
                <Separator />
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Tier</span>
                  <span className="font-semibold text-slate-900">{tierMeta.label}</span>
                </div>
                <Separator />
                <div className="flex justify-between gap-3">
                  <span className="text-slate-500">Slug</span>
                  <span className="font-mono text-xs text-slate-700">{vendor.slug}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'orders' && (
        <Card>
          <CardContent className="p-5">
            {vendor.recentOrders.length === 0 ? (
              <EmptyState icon={ShoppingBag} title="No orders" description="This vendor has not received any orders yet." />
            ) : (
              <ul className="divide-y divide-slate-100">
                {vendor.recentOrders.map((o) => (
                  <li key={o.id} className="flex items-center justify-between py-3">
                    <div>
                      <button
                        type="button"
                        onClick={() => navigate(`/orders/${o.id}`)}
                        className="text-sm font-semibold text-slate-900 hover:text-primary-600 tabular-nums"
                      >
                        #{o.orderNumber}
                      </button>
                      <p className="text-xs text-slate-500">{formatDate(o.createdAt)}</p>
                    </div>
                    <Badge variant="secondary" className="capitalize">{o.status.replace(/_/g, ' ')}</Badge>
                    <span className="text-sm font-bold text-slate-900 tabular-nums">{formatNPRCompact(o.total)}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'kyc' && (() => {
        const docs = Object.entries(vendor.documents ?? {}).filter(([, url]) => !!url);
        return (
          <Card>
            <CardContent className="p-5 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                  <span className="text-slate-500">PAN number</span>
                  <span className="font-mono font-semibold text-slate-900 tabular-nums">{vendor.panNumber ?? '—'}</span>
                </div>
                <div className="flex justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2">
                  <span className="text-slate-500">VAT number</span>
                  <span className="font-mono font-semibold text-slate-900 tabular-nums">{vendor.vatNumber ?? '—'}</span>
                </div>
              </div>

              {docs.length === 0 ? (
                <EmptyState
                  icon={FileText}
                  title="No KYC documents"
                  description="This vendor has not uploaded any verification documents yet."
                />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {docs.map(([key, value]) => (
                    <KycDocCard key={key} vendorId={id} docKey={key} storedValue={value} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      <ReasonModal
        open={modal === 'reject'}
        title="Reject vendor application"
        description="The vendor will see this reason and can resubmit their KYC."
        presets={REJECTION_REASONS}
        confirmLabel="Reject vendor"
        pending={rejectMutation.isPending}
        onConfirm={(reason) => { rejectMutation.mutate(reason); setModal(null); }}
        onClose={() => setModal(null)}
      />
      <ReasonModal
        open={modal === 'suspend'}
        title="Suspend vendor"
        description="Internal note. The vendor's store will be hidden until reinstated."
        presets={SUSPENSION_REASONS}
        confirmLabel="Suspend vendor"
        pending={suspendMutation.isPending}
        onConfirm={(reason) => { suspendMutation.mutate(reason); setModal(null); }}
        onClose={() => setModal(null)}
      />
    </div>
  );
}
